import React from 'react';
import ProjectBadge from '../../../components/common/ProjectBadge/ProjectBadge';
import ReactivatedBadge from '../../../components/common/ReactivatedBadge/ReactivatedBadge';
import SensitiveStudentBadge from '../../../components/common/SensitiveStudentBadge/SensitiveStudentBadge';
import PregnantStudentBadge from '../../../components/common/PregnantStudentBadge/PregnantStudentBadge';
import RedFlagBadge from '../../../components/common/RedFlagBadge/RedFlagBadge';
import ClassificationBadge from '../../../components/common/ClassificationBadge/ClassificationBadge';
import Tooltip from '../../../components/common/Tooltip';
import CopyStudentNameButton from '../../../components/common/CopyStudentNameButton/CopyStudentNameButton';
import type { CheckoutLead, ExistingStudentContactMatch } from '../../../types';
import {
  getStudentClassificationBadgeColors,
  shouldDisplayStudentClassificationBadge,
} from '../../../types';
import { formatDateTime } from '../../../utils';
import { maskPhoneByCountry } from '../../../utils/masks';
import { isCintaNaturalProject } from '../../../utils/project';
import StudentPublicLinkCopyButton from '../../Students/StudentRegister/StudentPublicLinkCopyButton';
import studentListStyles from '../../Students/StudentList/StudentList.module.css';
import styles from '../Onboarding.module.css';
import OnboardingFormStatusCell from './OnboardingFormStatusCell';

function formatLeadPhone(lead: CheckoutLead): string {
  const callingCode = lead.student_phone_country_code || '55';
  return maskPhoneByCountry(lead.student_phone || '', callingCode);
}

function openLeadWhatsApp(lead: CheckoutLead): void {
  if (!lead.student_phone) return;

  const callingCode = (lead.student_phone_country_code || '55').replace(/\D/g, '');
  const digits = lead.student_phone.replace(/\D/g, '');
  if (!digits) return;

  window.open(`https://wa.me/${callingCode}${digits}`, '_blank', 'noopener');
}

export function getOnboardingLeadRowKey(
  lead: Pick<CheckoutLead, 'student_id' | 'qualification_id' | 'checkout_id'>,
): string {
  if (lead.student_id != null && lead.student_id > 0) {
    return `student-${lead.student_id}`;
  }

  return String(lead.qualification_id ?? lead.checkout_id);
}

export interface OnboardingLeadRowCellsProps {
  lead: CheckoutLead;
  onOpenStudentDetails: (studentId: number | null) => void;
  onOpenExistingStudentMatch: (match: ExistingStudentContactMatch) => void;
  onOpenParqAlerts: (lead: CheckoutLead) => void;
}

/** Returns flat cell elements so DataTable.Row can label each column. */
export function buildOnboardingLeadRowCells({
  lead,
  onOpenStudentDetails,
  onOpenExistingStudentMatch,
  onOpenParqAlerts,
}: OnboardingLeadRowCellsProps): React.ReactElement[] {
  const lifecycleClassification = shouldDisplayStudentClassificationBadge(lead.classification)
    ? lead.classification
    : null;
  const lifecycleColors = lifecycleClassification
    ? getStudentClassificationBadgeColors(lifecycleClassification)
    : null;

  return [
    <div key="name" className={styles['col-name']}>
      <div className={styles['lead-name-row']}>
        {lead.student_id != null ? (
          <button
            type="button"
            className={styles['lead-name']}
            onClick={() => onOpenStudentDetails(lead.student_id)}
          >
            {lead.student_name}
          </button>
        ) : (
          <span
            className={styles['lead-name']}
            style={{ cursor: 'default' }}
          >
            {lead.student_name}
          </span>
        )}
        <CopyStudentNameButton studentName={lead.student_name} />
        {lead.existingStudentMatch && (
          <Tooltip content="Contato já cadastrado em outra aluna">
            <button
              type="button"
              className={styles['existing-student-warning']}
              onClick={() => onOpenExistingStudentMatch(lead.existingStudentMatch!)}
              aria-label="Contato já cadastrado em outra aluna"
            >
              <span className={studentListStyles['warning-icon']} aria-hidden="true">
                warning
              </span>
            </button>
          </Tooltip>
        )}
      </div>
      {lead.student_email ? (
        <span className={styles['lead-email']}>{lead.student_email}</span>
      ) : (
        <span className={styles['lead-email-empty']}>—</span>
      )}
      {lead.student_phone && (
        <span className={styles['lead-phone']}>
          <Tooltip content="Abrir conversa no WhatsApp">
            <button
              type="button"
              className={studentListStyles['btn-whatsapp']}
              aria-label="Abrir conversa no WhatsApp"
              onClick={() => openLeadWhatsApp(lead)}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </button>
          </Tooltip>
          {formatLeadPhone(lead)}
        </span>
      )}
    </div>,

    <div key="tags" className={styles['col-tags']}>
      {(lead.project || (lifecycleClassification && lifecycleColors) || lead.is_reactivated || lead.is_sensitive || lead.is_pregnant || lead.is_red_flag) ? (
        <>
          <ProjectBadge project={lead.project} />
          {lifecycleClassification && lifecycleColors && (
            <ClassificationBadge
              label={lifecycleClassification.label}
              backgroundColor={lifecycleColors.background}
              textColor={lifecycleColors.text}
            />
          )}
          <ReactivatedBadge visible={lead.is_reactivated} />
          <SensitiveStudentBadge visible={lead.is_sensitive} reason={lead.sensitive_reason} />
          <PregnantStudentBadge visible={lead.is_pregnant} pregnantUntil={lead.pregnant_until} />
          <RedFlagBadge visible={lead.is_red_flag} />
        </>
      ) : (
        <span className={styles['tags-empty']}>—</span>
      )}
    </div>,

    <div key="paid-at" className={styles['col-center']}>
      <span>
        {lead.active_subscription_paid_at
          ? formatDateTime(lead.active_subscription_paid_at)
          : '—'}
      </span>
    </div>,

    <div key="link" className={styles['col-link']}>
      {lead.student_id != null ? (
        <>
          <StudentPublicLinkCopyButton
            studentId={lead.student_id}
            linkTarget="onboarding"
            label="Onboarding"
          />
          {isCintaNaturalProject(lead.project) && (
            <StudentPublicLinkCopyButton
              studentId={lead.student_id}
              linkTarget="cinta-natural"
              label="Envio de materiais"
            />
          )}
        </>
      ) : (
        <span>—</span>
      )}
    </div>,

    <div key="anamnesis" className={styles['col-status']}>
      <OnboardingFormStatusCell
        active={lead.has_anamnesis}
        activeLabel="Anamnese preenchida"
        missingLabel="Anamnese não preenchida"
        studentId={lead.student_id}
        linkTarget="anamnese"
      />
    </div>,

    <div key="parq" className={styles['col-status']}>
      {lead.parq_has_yes_answers ? (
        <Tooltip content="Ver perguntas do PAR-Q respondidas com Sim">
          <button
            type="button"
            className={styles['parq-alert-link']}
            onClick={() => onOpenParqAlerts(lead)}
            aria-label="Ver perguntas do PAR-Q respondidas com Sim"
          >
            <span className={studentListStyles['warning-icon']} aria-hidden="true">warning</span>
          </button>
        </Tooltip>
      ) : (
        <OnboardingFormStatusCell
          active={lead.has_parq}
          activeLabel="PAR-Q preenchido"
          missingLabel="PAR-Q não preenchido"
          studentId={lead.student_id}
          linkTarget="parq"
        />
      )}
    </div>,

    <div key="photos" className={styles['col-status']}>
      <OnboardingFormStatusCell
        active={lead.has_photos}
        activeLabel="Fotos enviadas"
        missingLabel="Fotos não enviadas"
        studentId={lead.student_id}
        linkTarget="enviar-fotos"
      />
    </div>,
  ];
}
